import React, { createContext, useContext, ReactNode, useState } from 'react'
import { useAuth } from '../auth-context/AuthContext'
import {
  apiResponse,
  createDownloadLink,
  ResponseType,
  sanitizeFilename,
  showSnackbar,
} from '../../utils/Utils'
import { useFetchWithAuth } from '../../hooks/useFetchWithAuth'
import { VariantType } from '../../lib/CommonLib'
import { UploadDataType } from '../../lib/UploadLib'

interface UploadViewContextType {
  loading: boolean
  downloadTemplate: (val: number) => Promise<void>
  uploadFile: (
    file: any,
    dataType: number,
    uploadYear: string,
    onStatusChange?: (status: string) => void
  ) => Promise<boolean>
  uploadAdditionalFile: (
    file: any,
    dataType: number,
    onStatusChange?: (status: string) => void
  ) => Promise<void>
}

const UploadContext = createContext<UploadViewContextType | undefined>(undefined)

export const UploadViewProvider = ({ children }: { children: ReactNode }) => {
  const { setSnackbarOpen, setSnackbarMessage, setVariant } = useAuth()
  const fetchWithAuth = useFetchWithAuth()
  const [fileId, setFileId] = useState<any>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const downloadTemplate = async (dataType: number) => {
    try {
      setLoading(true)
      const response = await fetchWithAuth(`/api/Upload/DownloadTemplate?dataType=${dataType}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const blob = await apiResponse(response, ResponseType.blob)
      const contentDisposition = response.headers.get('content-disposition')

      let filename = 'Template.xlsx'

      switch (dataType) {
        case UploadDataType.SampleInfo:
          filename = 'SampleInfoTemplate.zip'
          break
        case UploadDataType.PlanktonSurveyResult:
          filename = 'PlanktonSurveyTemplate.zip'
          break
        case UploadDataType.NutrientSurveyResult:
          filename = 'NutrientSurveyTemplate.xlsx'
          break
        case UploadDataType.SedimentQualityResult:
          filename = 'SedimentQualityTemplate.xlsx'
          break
        case UploadDataType.HeavyMetals:
          filename = 'MLS-IMS WQ Reporting Template-Heavy Metals.xlsx'
          break
        case UploadDataType.PetroleumHydrocarbons:
          filename = 'MLS-IMS WQ Reporting Template-PAH OG.xlsx'
          break
      }

      if (contentDisposition) {
        const matches = contentDisposition.match(/filename\*?=(?:UTF-8''([^;]+)|\"([^\"]+)\")/)
        if (matches != null) {
          filename = decodeURIComponent(matches[1] || matches[2])
        }
      }

      filename = sanitizeFilename(filename)
      const downloadUrl = window.URL.createObjectURL(blob)
      createDownloadLink(downloadUrl, filename)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
    } finally {
      setLoading(false)
    }
  }

  const uploadFile = async (
    file: any,
    dataType: number,
    uploadYear: string,
    onStatusChange?: (status: string) => void
  ) => {
    try {
      setLoading(true)
      onStatusChange?.('Uploading...')

      const formData = new FormData()
      formData.append('file', file)
      const response = await fetchWithAuth(
        `/api/Upload?dataType=${dataType}&uploadYear=${uploadYear}`,
        {
          method: 'POST',
          body: formData,
        }
      )

      onStatusChange?.('Processing upload...')
      const uploadId = await apiResponse(response)
      setFileId(uploadId?.id)

      onStatusChange?.('Upload completed successfully')
      showSnackbar(
        'Document successfully uploaded',
        VariantType.success,
        setVariant,
        setSnackbarOpen,
        setSnackbarMessage
      )

      return true
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      onStatusChange?.(`Upload failed: ${errorMessage}`)
      showSnackbar(errorMessage, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
      return false
    } finally {
      setLoading(false)
    }
  }

  const uploadAdditionalFile = async (
    files: any,
    dataType: number,
    onStatusChange?: (status: string) => void
  ) => {
    try {
      setLoading(true)
      onStatusChange?.('Uploading additional files...')

      const formData = new FormData()
      files.forEach((file: any) => formData.append('files', file))
      const cleanUploadId = fileId.trim().replace(/"/g, '')

      const response = await fetchWithAuth(
        `/api/Upload/AdditionalFiles?mainUploadId=${cleanUploadId}&dataType=${dataType}`,
        {
          method: 'POST',
          body: formData,
        }
      )

      if (!response.ok) {
        let errorDetail = 'Failed to upload document'
        try {
          const errorResponse = await response.json()
          errorDetail = errorResponse.detail || errorDetail
        } catch (jsonError) {
          errorDetail = 'Failed to upload document'
        }
        onStatusChange?.(`Upload failed: ${errorDetail}`)
        throw new Error(errorDetail)
      }

      onStatusChange?.('Processing additional files...')
      apiResponse(response)
      onStatusChange?.('Additional files uploaded successfully')
      showSnackbar(
        'Document successfully uploaded',
        VariantType.success,
        setVariant,
        setSnackbarOpen,
        setSnackbarMessage
      )
    } catch (error) {
      onStatusChange?.(`Upload failed: ${error}`)
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <UploadContext.Provider
      value={{
        loading,
        downloadTemplate,
        uploadFile,
        uploadAdditionalFile,
      }}
    >
      {children}
    </UploadContext.Provider>
  )
}

export const useUploadView = () => {
  const context = useContext(UploadContext)
  if (context === undefined) {
    throw new Error('useUploadView must be used within a UploadViewProvider')
  }
  return context
}
